//@flow
import * as React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import getter from 'dibs-object-getter';
import { FormattedMessage } from 'react-intl';
import { profileAboutMutation } from '../mutations/profileAbout';
import { ExpandingArea } from 'dibs-elements/exports/ExpandingArea';
import { TextArea } from 'dibs-elements/exports/TextArea';
import { GridCol } from 'dibs-elements/exports/Grid';
import Pen from 'dibs-icons/exports/Pen';
import { logError } from '../../helpers';
import styles from './OverviewAbout.scss';

import type { OverviewAbout_tradeFirm as TradeFirmType } from './__generated__/OverviewAbout_tradeFirm.graphql';
import type { RelayProp } from 'react-relay';

type Props = {
    isEditMode: boolean,
    tradeFirm: ?TradeFirmType,
    relay?: RelayProp,
};

type State = {
    expandCollapseLabel: React.Element,
    isExpanded: boolean,
    isEditing: boolean,
    isSaving: boolean,
    hasError: boolean,
    about: ?string,
};

const MAX_DESCRIPTION_LENGTH = 2500;
const TEXT_AREA_ROWS = 14;

type ReadOnlyAboutType = {
    isExpanded: boolean,
    cta: React.Element,
    onClick: (SyntheticEvent<HTMLAnchorElement>) => void,
    shown: Array<string>,
    hidden: Array<string>,
};

const ReadMore = (
    <FormattedMessage defaultMessage="Read More +" id="abt.designProfile.About.read_more" />
);

const ReadLess = (
    <FormattedMessage defaultMessage="Read Less -" id="abt.designProfile.About.read_less" />
);

const ABOUT_MAX_LENGTH = 700;

const createParagraphKey = (
    index: number,
    paragraph?: string,
    prefix?: string
): string | number => {
    const key = paragraph;
    if (key) {
        return key;
    }
    // even index 0 should be accounted for
    if (prefix && index > -1) {
        // empty line
        return `${prefix}-${index}`;
    }
    return index;
};

const createShowHideData = (display: Array<string>) => {
    let charactersLeft = ABOUT_MAX_LENGTH;
    const shown = [];
    const hidden = [];
    display.forEach((paragraph, index) => {
        if (paragraph) {
            if (index === 0) {
                // always push the first paragraph
                shown.push(paragraph);
            } else if (charactersLeft > 0 && paragraph.length <= charactersLeft) {
                shown.push(paragraph);
            } else {
                hidden.push(paragraph);
            }
            charactersLeft = charactersLeft - paragraph.length;
        }
    });
    return {
        shown,
        hidden,
    };
};

const ReadOnlyAbout = ({ isExpanded, cta, onClick, shown, hidden }: ReadOnlyAboutType) => {
    let wrapperClass = '';
    if (isExpanded) {
        wrapperClass = styles.aboutExpanded;
    }
    return (
        <React.Fragment>
            {shown.map((para, index) => {
                const key = createParagraphKey(index, para, 'about-shown');
                return (
                    <p key={key} className={styles.paragraph}>
                        {para}
                    </p>
                );
            })}
            <ExpandingArea
                dataTn="design-firm-about"
                showArrow={false}
                showOverflow
                expanded={isExpanded}
                wrapperClass={wrapperClass}
            >
                {hidden.map((para, index) => {
                    const key = createParagraphKey(index, para, 'about-hidden');
                    return (
                        <p key={key} className={styles.paragraph}>
                            {para}
                        </p>
                    );
                })}
            </ExpandingArea>
            {hidden.length > 0 && (
                <a href="#" onClick={onClick} data-tn="design-firm-about-cta">
                    {cta}
                </a>
            )}
        </React.Fragment>
    );
};

type EditAboutType = {
    isEditing: boolean,
    isEditMode: boolean,
    isSaving: boolean,
    copy: Array<string>,
    editingOnBlur: (event: SyntheticFocusEvent<HTMLTextAreaElement>) => void,
    editModeOnClick: () => void,
};

const EditAbout = ({
    isEditing,
    isEditMode,
    isSaving,
    copy,
    editingOnBlur,
    editModeOnClick,
}: EditAboutType) => {
    if (isEditing) {
        return (
            <div className={styles.wrapper}>
                <TextArea
                    name="overview-about-edit"
                    dataTn="overview-about-edit-data-tn"
                    onBlur={editingOnBlur}
                    value={copy.join('\n')}
                    autoFocus
                    disabled={isSaving}
                    rows={TEXT_AREA_ROWS}
                    maxLength={MAX_DESCRIPTION_LENGTH}
                />
            </div>
        );
    } else if (isEditMode) {
        copy = copy.filter(Boolean);
        return (
            <React.Fragment>
                <div className={styles.wrapper} onClick={editModeOnClick}>
                    {copy && !copy.length ? (
                        <div>
                            <FormattedMessage
                                id="abt.overview.about.placeholder"
                                defaultMessage="Provide a description of your firm here."
                            />
                        </div>
                    ) : (
                        copy.map((para, index) => {
                            const key = createParagraphKey(index, para, 'about-copy');
                            return (
                                <p key={key} className={styles.paragraph}>
                                    {para}
                                </p>
                            );
                        })
                    )}
                </div>
                <Pen className={styles.pen} />
            </React.Fragment>
        );
    }
    return null;
};

export class OverviewAboutComponent extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const about =
            getter(this.props.tradeFirm)
                .at('publicProfile')
                .value('about') || '';
        this.state = {
            expandCollapseLabel: ReadMore,
            isExpanded: false,
            isEditing: false,
            isSaving: false,
            hasError: false,
            about,
        };

        this.onAboutExpandCollapseClick = this.onAboutExpandCollapseClick.bind(this);
        this.onFinishEditing = this.onFinishEditing.bind(this);
    }

    onStartEditing = (): void => {
        if (this.props.isEditMode) {
            this.setState({ isEditing: true });
        }
    };

    onFinishEditing: (event: SyntheticFocusEvent<HTMLTextAreaElement>) => void;
    onFinishEditing(event: SyntheticFocusEvent<HTMLTextAreaElement>) {
        if (!this.props.isEditMode) {
            return;
        }

        /*
         * The `about` text is passed to the backend as a JSON-encoded string, so take the
         * output from the text field, split it on any line breaks, and then sent that assemblage
         * to the BE
         *
         * eg: ["This is paragraph 1.","This is paragraph 2."]
         */

        const about = JSON.stringify((event.currentTarget.value || '').split(/\r|\n/));
        const firmId = getter(this.props.tradeFirm).value('serviceId');

        this.setState({ isSaving: true });

        if (this.props.relay && firmId) {
            profileAboutMutation(this.props.relay.environment, {
                firmId,
                about,
            })
                .then(() => {
                    this.setState({ isEditing: false, hasError: false, isSaving: false });
                })
                .catch(e => {
                    logError(e, 'Caught exception when saving about text');
                    this.setState({ isSaving: false, hasError: true });
                    return;
                });
        }
    }

    onAboutExpandCollapseClick: (event: SyntheticEvent<HTMLAnchorElement>) => void;
    onAboutExpandCollapseClick(event: SyntheticEvent<HTMLAnchorElement>) {
        event.preventDefault();
        const { isExpanded } = this.state;
        // change expanded state before
        // determining which cta/label to use
        const newExpandedState = !isExpanded;
        const label = newExpandedState ? ReadLess : ReadMore;
        this.setState({
            isExpanded: newExpandedState,
            expandCollapseLabel: label,
        });
    }
    render() {
        const about =
            getter(this.props.tradeFirm)
                .at('publicProfile')
                .value('about') || '';

        // This string may or may not be JSON-encoded (see the comment above in onFinishEditing)
        let display = [about];
        let readOnlyOutput = {};
        try {
            const lastIndex = about.length - 1;
            if (about.indexOf('[') === 0 && about.indexOf(']') === lastIndex) {
                display = JSON.parse(about);
            }
            if (!this.props.isEditMode) {
                readOnlyOutput = createShowHideData(display);
            }
        } catch (e) {
            // Swallow errors on failing to parse -- some strings are still flat strings
            // without JSON encoding.
            console.warn(e);
        }
        display = display.filter(Boolean);

        const editClass =
            this.props.isEditMode && !this.state.isEditing ? styles.editContainer : '';

        return (
            <GridCol tabletLandscape={8} mobile={12} mobileOptions={{ verticalAlign: 'stretch' }}>
                <div className={editClass} data-tn="about-container" onClick={this.onStartEditing}>
                    {this.state.isEditing || this.props.isEditMode ? (
                        <EditAbout
                            isEditing={this.state.isEditing}
                            isEditMode={this.props.isEditMode}
                            isSaving={this.state.isSaving}
                            editingOnBlur={this.onFinishEditing}
                            editModeOnClick={this.onStartEditing}
                            copy={display}
                        />
                    ) : (
                        <div className={styles.wrapper}>
                            <ReadOnlyAbout
                                isExpanded={this.state.isExpanded}
                                onClick={this.onAboutExpandCollapseClick}
                                cta={this.state.expandCollapseLabel}
                                {...readOnlyOutput}
                            />
                        </div>
                    )}
                </div>
            </GridCol>
        );
    }
}

export const OverviewAbout = createFragmentContainer(OverviewAboutComponent, {
    tradeFirm: graphql`
        fragment OverviewAbout_tradeFirm on TradeFirmType {
            serviceId
            publicProfile {
                about
            }
        }
    `,
});
